import { Box } from "ui/box/box";
import { Button } from "ui/button/button";
import { LoadingButton } from "ui/button/loading-button/loading-button";
import { Dialog } from "ui/dialog/dialog";
import { DialogActions } from "ui/dialog/dialog-actions/dialog-actions";

import { useConfirmationDialog } from "ui/confirmation-dialog/use-confirmation-dialog";

import { type ConfirmationDialogProps } from "ui/confirmation-dialog/confirmation-dialog.types";

/** A small `Dialog` preset whose footer is a confirm and a cancel button. */
export const ConfirmationDialog = ({
  onClose,
  onConfirm,
  message,
  confirmButtonText = "Submit",
  cancelButtonText = "Cancel",
  children,
  disabled = false,
  confirming = false,
  confirmOnEnter = true,
  confirmLoadingMessage = "Processing...",
  onKeyDown,
  ...dialogProps
}: ConfirmationDialogProps) => {
  const { isPending, handleConfirm, handleKeyDown } = useConfirmationDialog({
    onConfirm,
    onKeyDown,
    confirming,
    disabled,
    confirmOnEnter,
  });

  const confirmButton = (
    <LoadingButton
      state={isPending ? "loading" : "default"}
      loadingMessage={confirmLoadingMessage}
      onClick={handleConfirm}
      data-testid="modal-confirm-button"
      size="large"
      disabled={disabled}>
      {confirmButtonText}
    </LoadingButton>
  );

  const cancelButton = (
    <Button
      variant="outlined"
      onClick={onClose}
      size="large"
      data-testid="modal-cancel-button">
      {cancelButtonText}
    </Button>
  );

  return (
    <Dialog
      {...dialogProps}
      onClose={onClose}
      size="small"
      description={message}
      actions={<DialogActions primary={confirmButton} secondary={cancelButton} />}
      onKeyDown={handleKeyDown}>
      {children && <Box>{children}</Box>}
    </Dialog>
  );
};
